import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { Avatar, Input, Button, Spin, notification, Grid } from 'antd';
import { SendOutlined, MessageOutlined, LoadingOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import CustomNavbar from '../components/Navbar';
import { initEcho } from '../echo';
import api from '../api/axios';
import { useProfile } from '../context/ProfileContext';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(utc);
dayjs.extend(relativeTime);

const { useBreakpoint } = Grid;

// A contact is "online" if they had API activity within the last 5 minutes.
const isOnline = (lastActivityAt) =>
    lastActivityAt && dayjs().diff(dayjs(lastActivityAt), 'minute') < 5;

const Inbox = () => {
    const screens = useBreakpoint();
    // On mobile we show either the sidebar OR the chat, not both
    const isMobile = !screens.md;

    const [contacts, setContacts] = useState([]);
    const [selectedContact, setSelectedContact] = useState(null);
    const [chats, setChats] = useState([]);
    const [newMessage, setNewMessage] = useState('');
    const [isLoadingContacts, setIsLoadingContacts] = useState(true);
    const [isLoadingChats, setIsLoadingChats] = useState(false);
    const [isSending, setIsSending] = useState(false);
    // Mobile only: which panel is visible — 'contacts' | 'chat'
    const [mobilePanel, setMobilePanel] = useState('contacts');
    const messagesEndRef = useRef(null);
    const selectedContactRef = useRef(null);

    const { profileId: loggedInProfileId, profileDbId } = useProfile();

    const fetchContacts = useCallback(async () => {
        if (!loggedInProfileId) return;
        setIsLoadingContacts(true);
        try {
            const response = await api.get('/messages/show/contacts', {
                params: { profile_id: loggedInProfileId },
            });
            if (response.data.status === 'success') {
                const freshContacts = response.data.data;
                setContacts(freshContacts);
                // Keep selectedContact in sync so active status updates in the chat header
                if (selectedContactRef.current) {
                    const fresh = freshContacts.find(
                        c => c.profile_id === selectedContactRef.current.profile_id
                    );
                    if (fresh) setSelectedContact(fresh);
                }
            } else {
                notification.error({ message: 'Failed to load contacts', description: response.data.message });
            }
        } catch {
            notification.error({ message: 'Failed to load contacts', description: 'Please try again.' });
        } finally {
            setIsLoadingContacts(false);
        }
    }, [loggedInProfileId]);

    const fetchChats = useCallback(async (otherProfileId, silent = false) => {
        if (!silent) setIsLoadingChats(true);
        try {
            const response = await api.get('/messages/show/chats', {
                params: { profile_id: loggedInProfileId, other_profile_id: otherProfileId },
            });
            if (response.data.status === 'success') {
                const msgs = response.data.data;
                setChats(msgs);
                // Sync contact preview with the actual latest message
                if (msgs.length > 0) {
                    const latest = msgs[msgs.length - 1];
                    setContacts(prev => prev.map(c =>
                        c.profile_id === otherProfileId
                            ? { ...c, message: latest.message, created_at: latest.created_at }
                            : c
                    ));
                }
            }
        } catch {
            if (!silent) notification.error({ message: 'Failed to load messages', description: 'Please try again.' });
        } finally {
            if (!silent) setIsLoadingChats(false);
        }
    }, [loggedInProfileId]);

    const handleSelectContact = (contact) => {
        setSelectedContact(contact);
        selectedContactRef.current = contact;
        fetchChats(contact.profile_id);
        if (isMobile) setMobilePanel('chat');
    };

    const handleBackToContacts = () => {
        setMobilePanel('contacts');
        setSelectedContact(null);
        selectedContactRef.current = null;
        setChats([]);
    };

    const handleSendMessage = async () => {
        if (!newMessage.trim()) return;

        const payload = {
            sender_id: loggedInProfileId,
            receiver_id: selectedContact.profile_id,
            message: newMessage.trim(),
        };

        setIsSending(true);
        try {
            const response = await api.post('/messages/send', payload);
            if (response.data.status === 'success') {
                setChats(prev => [...prev, {
                    id: response.data.data.id,
                    sender_id: payload.sender_id,
                    receiver_id: payload.receiver_id,
                    message: payload.message,
                    created_at: response.data.data.created_at,
                }]);
                setContacts(prev => prev.map(c =>
                    c.profile_id === selectedContact.profile_id
                        ? { ...c, message: payload.message, created_at: response.data.data.created_at }
                        : c
                ));
                setNewMessage('');
            } else {
                notification.error({ message: 'Failed to send', description: response.data.message });
            }
        } catch {
            notification.error({ message: 'Failed to send', description: 'Please try again.' });
        } finally {
            setIsSending(false);
        }
    };

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [chats]);

    useEffect(() => {
        fetchContacts();
    }, [fetchContacts]);

    // Poll contacts every 60 s to keep active/last-seen status fresh
    useEffect(() => {
        if (!loggedInProfileId) return;
        const interval = setInterval(fetchContacts, 60_000);
        return () => clearInterval(interval);
    }, [loggedInProfileId, fetchContacts]);

    // Real-time: listen for incoming messages via WebSocket
    useEffect(() => {
        if (!profileDbId) return;

        const echo = initEcho();
        const channel = echo.private(`profile.${profileDbId}`);

        channel.listen('.message.received', (payload) => {
            const senderPublicId = String(payload.actor_profile_id);
            // Always refresh contacts list to update the preview
            fetchContacts();
            // If this sender's chat is currently open, silently refresh messages
            if (String(selectedContactRef.current?.profile_id) === senderPublicId) {
                fetchChats(senderPublicId, true);
            }
        });

        return () => {
            channel.stopListening('.message.received');
        };
    }, [profileDbId, fetchContacts, fetchChats]);

    const groupMessagesByDate = (msgs) => {
        const grouped = {};
        msgs.forEach((msg) => {
            const date = dayjs(msg.created_at).utc().format('YYYY-MM-DD');
            if (!grouped[date]) grouped[date] = [];
            grouped[date].push(msg);
        });
        return grouped;
    };

    const formatDateLabel = (dateStr) => {
        const today = dayjs().format('YYYY-MM-DD');
        const yesterday = dayjs().subtract(1, 'day').format('YYYY-MM-DD');
        if (dateStr === today) return 'Today';
        if (dateStr === yesterday) return 'Yesterday';
        return dayjs(dateStr).format('MMMM D, YYYY');
    };

    const groupedMessages = groupMessagesByDate(chats);

    // ─── Sub-components ────────────────────────────────────────────────────────

    const ContactsSidebar = () => (
        <div style={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            background: '#fff',
            borderRight: isMobile ? 'none' : '1px solid var(--color-border)',
        }}>
            {/* Sidebar header */}
            <div style={{
                padding: '18px 20px 14px',
                background: 'var(--gradient-primary)',
                flexShrink: 0,
            }}>
                <h2 style={{ color: '#fff', margin: 0, fontSize: '1.15rem', fontWeight: 700 }}>Messages</h2>
                <p style={{ color: 'rgba(255,255,255,0.75)', margin: '3px 0 0', fontSize: '0.82rem' }}>
                    {contacts.length} conversation{contacts.length !== 1 ? 's' : ''}
                </p>
            </div>

            {/* Contact list */}
            <div style={{ flex: 1, overflowY: 'auto' }}>
                {isLoadingContacts ? (
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '120px' }}>
                        <Spin indicator={<LoadingOutlined style={{ fontSize: 28, color: 'var(--color-primary)' }} spin />} />
                    </div>
                ) : contacts.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '48px 20px', color: 'var(--color-text-muted)' }}>
                        <MessageOutlined style={{ fontSize: '36px', marginBottom: '10px', display: 'block' }} />
                        <p style={{ margin: 0, fontSize: '0.9rem' }}>No conversations yet</p>
                    </div>
                ) : (
                    contacts.map((contact) => {
                        const isSelected = !isMobile && selectedContact?.profile_id === contact.profile_id;
                        const avatar = contact.image_path
                            ? `${process.env.REACT_APP_IMAGE_URL}/${contact.image_path}`
                            : (contact.gender?.toLowerCase() === 'female' ? process.env.REACT_APP_FEMALE_FALLBACK_IMAGE : process.env.REACT_APP_MALE_FALLBACK_IMAGE);

                        return (
                            <div
                                key={contact.profile_id}
                                onClick={() => handleSelectContact(contact)}
                                style={{
                                    display: 'flex', alignItems: 'center', gap: '12px',
                                    padding: '14px 20px',
                                    cursor: 'pointer',
                                    background: isSelected ? 'var(--color-primary-faint)' : 'transparent',
                                    borderLeft: isSelected ? '3px solid var(--color-primary)' : '3px solid transparent',
                                    borderBottom: '1px solid var(--color-border)',
                                    transition: 'background 0.15s ease',
                                }}
                            >
                                <div style={{ position: 'relative', flexShrink: 0 }}>
                                    <Avatar src={avatar} size={46} style={{ border: '2px solid var(--color-primary-lighter)' }} />
                                    {isOnline(contact.last_activity_at) && (
                                        <span style={{
                                            position: 'absolute', bottom: 1, right: 1,
                                            width: '11px', height: '11px',
                                            background: 'var(--color-success)',
                                            border: '2px solid #fff', borderRadius: '50%',
                                        }} />
                                    )}
                                </div>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '6px' }}>
                                        <span style={{ fontWeight: 600, fontSize: '0.93rem', color: 'var(--color-text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            {contact.first_name} {contact.last_name}
                                        </span>
                                        {contact.created_at && (
                                            <span style={{ fontSize: '0.7rem', color: 'var(--color-text-muted)', flexShrink: 0 }}>
                                                {dayjs(contact.created_at).fromNow(true)}
                                            </span>
                                        )}
                                    </div>
                                    <p style={{
                                        margin: 0, fontSize: '0.8rem',
                                        color: 'var(--color-text-secondary)',
                                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                    }}>
                                        {contact.message || 'No messages yet'}
                                    </p>
                                </div>
                            </div>
                        );
                    })
                )}
            </div>
        </div>
    );

    const ChatPanel = () => (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            {/* Chat header */}
            <div style={{
                padding: isMobile ? '12px 16px' : '14px 24px',
                background: '#fff',
                borderBottom: '1px solid var(--color-border)',
                display: 'flex', alignItems: 'center', gap: '12px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                flexShrink: 0,
            }}>
                {isMobile && (
                    <Button
                        type="text"
                        icon={<ArrowLeftOutlined />}
                        onClick={handleBackToContacts}
                        style={{ color: 'var(--color-primary)', padding: '0 4px', flexShrink: 0 }}
                    />
                )}
                <div style={{ position: 'relative', flexShrink: 0 }}>
                    <Avatar
                        src={selectedContact.image_path
                            ? `${process.env.REACT_APP_IMAGE_URL}/${selectedContact.image_path}`
                            : (selectedContact.gender?.toLowerCase() === 'female' ? process.env.REACT_APP_FEMALE_FALLBACK_IMAGE : process.env.REACT_APP_MALE_FALLBACK_IMAGE)}
                        size={isMobile ? 38 : 46}
                        style={{ border: '2px solid var(--color-primary-lighter)' }}
                    />
                    {isOnline(selectedContact.last_activity_at) && (
                        <span style={{
                            position: 'absolute', bottom: 1, right: 1,
                            width: '10px', height: '10px',
                            background: 'var(--color-success)',
                            border: '2px solid #fff', borderRadius: '50%',
                        }} />
                    )}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: isMobile ? '0.95rem' : '1rem', color: 'var(--color-text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {selectedContact.first_name} {selectedContact.last_name}
                    </div>
                    <div style={{ fontSize: '0.78rem', color: isOnline(selectedContact.last_activity_at) ? 'var(--color-success)' : 'var(--color-text-muted)' }}>
                        {isOnline(selectedContact.last_activity_at)
                            ? 'Active now'
                            : selectedContact.last_activity_at
                                ? `Last seen ${dayjs(selectedContact.last_activity_at).fromNow()}`
                                : 'Offline'}
                    </div>
                </div>
            </div>

            {/* Messages */}
            <div style={{
                flex: 1, overflowY: 'auto',
                padding: isMobile ? '14px 12px' : '20px 24px',
                background: '#FAFAFA',
                backgroundImage: 'radial-gradient(var(--color-primary-lighter) 1px, transparent 1px)',
                backgroundSize: '28px 28px',
            }}>
                {isLoadingChats ? (
                    <div style={{ display: 'flex', justifyContent: 'center', paddingTop: '40px' }}>
                        <Spin indicator={<LoadingOutlined style={{ fontSize: 32, color: 'var(--color-primary)' }} spin />} />
                    </div>
                ) : (
                    Object.keys(groupedMessages).map((date) => (
                        <div key={date}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', margin: '16px 0 12px' }}>
                                <div style={{ flex: 1, height: '1px', background: 'var(--color-border)' }} />
                                <span style={{
                                    fontSize: '0.72rem', fontWeight: 600,
                                    color: 'var(--color-text-muted)',
                                    padding: '2px 10px',
                                    border: '1px solid var(--color-border)',
                                    borderRadius: '20px',
                                    background: '#FAFAFA',
                                    whiteSpace: 'nowrap',
                                }}>
                                    {formatDateLabel(date)}
                                </span>
                                <div style={{ flex: 1, height: '1px', background: 'var(--color-border)' }} />
                            </div>

                            {groupedMessages[date].map((msg) => {
                                const isMine = msg.sender_id === loggedInProfileId;
                                return (
                                    <div key={msg.id} style={{
                                        display: 'flex',
                                        justifyContent: isMine ? 'flex-end' : 'flex-start',
                                        marginBottom: '6px',
                                    }}>
                                        <div style={{ maxWidth: isMobile ? '80%' : '62%' }}>
                                            <div style={{
                                                padding: '9px 14px',
                                                borderRadius: isMine ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
                                                background: isMine ? 'var(--gradient-primary)' : '#fff',
                                                color: isMine ? '#fff' : 'var(--color-text-primary)',
                                                boxShadow: '0 2px 6px rgba(0,0,0,0.07)',
                                                fontSize: isMobile ? '0.88rem' : '0.92rem',
                                                lineHeight: 1.5,
                                                wordBreak: 'break-word',
                                            }}>
                                                {msg.message}
                                            </div>
                                            <div style={{
                                                fontSize: '0.68rem',
                                                color: 'var(--color-text-muted)',
                                                marginTop: '2px',
                                                textAlign: isMine ? 'right' : 'left',
                                                paddingInline: '4px',
                                            }}>
                                                {dayjs(msg.created_at).format('h:mm A')}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ))
                )}
                <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div style={{
                padding: isMobile ? '10px 12px' : '14px 24px',
                background: '#fff',
                borderTop: '1px solid var(--color-border)',
                flexShrink: 0,
            }}>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <Input
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        onPressEnter={handleSendMessage}
                        placeholder="Type a message…"
                        size="large"
                        style={{ borderRadius: '24px', flex: 1, background: '#F8F8F8', fontSize: '0.92rem' }}
                    />
                    <Button
                        type="primary"
                        shape="circle"
                        size="large"
                        icon={<SendOutlined />}
                        onClick={handleSendMessage}
                        loading={isSending}
                        disabled={!newMessage.trim()}
                        style={{ width: '44px', height: '44px', flexShrink: 0 }}
                    />
                </div>
            </div>
        </div>
    );

    const EmptyState = () => (
        <div style={{
            height: '100%', display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center',
            background: '#FAFAFA', gap: '12px',
        }}>
            <div style={{
                width: '72px', height: '72px', borderRadius: '50%',
                background: 'var(--color-primary-faint)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
                <MessageOutlined style={{ fontSize: '32px', color: 'var(--color-primary)' }} />
            </div>
            <p style={{ fontSize: '1.05rem', fontWeight: 600, color: 'var(--color-text-primary)', margin: 0 }}>
                Your messages
            </p>
            <p style={{ fontSize: '0.88rem', color: 'var(--color-text-muted)', margin: 0 }}>
                Select a conversation to start chatting
            </p>
        </div>
    );

    // ─── Layout ────────────────────────────────────────────────────────────────

    return (
        <div style={{ fontFamily: 'Calibri, sans-serif', height: '100vh', display: 'flex', flexDirection: 'column', background: 'var(--color-bg)' }}>
            <Helmet><title>Messages | Oottan</title></Helmet>
            <CustomNavbar />

            <div style={{ display: 'flex', flex: 1, marginTop: '64px', overflow: 'hidden' }}>
                {isMobile ? (
                    // ── Mobile: one panel at a time ──
                    <div style={{ flex: 1, overflow: 'hidden' }}>
                        {mobilePanel === 'contacts' ? (
                            ContactsSidebar()
                        ) : (
                            ChatPanel()
                        )}
                    </div>
                ) : (
                    // ── Desktop: side-by-side ──
                    <>
                        <div style={{ width: '320px', flexShrink: 0, overflow: 'hidden' }}>
                            {ContactsSidebar()}
                        </div>
                        <div style={{ flex: 1, overflow: 'hidden' }}>
                            {selectedContact ? ChatPanel() : EmptyState()}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default Inbox;
